import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../../utils/authFetch';

// Fetch dashboard stats
export const fetchDashboardStats = createAsyncThunk(
  'agencyAssessments/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-dashboard-stats', { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Fetch all assessments
export const fetchAssessments = createAsyncThunk(
  'agencyAssessments/fetchAll',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.domain) params.set('domain', filters.domain);
      const qs = params.toString();
      const response = await authFetch(`/.netlify/functions/agency-assessments-list${qs ? `?${qs}` : ''}`, { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Fetch assessment detail
export const fetchAssessmentDetail = createAsyncThunk(
  'agencyAssessments/fetchDetail',
  async (assessmentId, { rejectWithValue }) => {
    try {
      const response = await authFetch(`/.netlify/functions/agency-assessments-detail?id=${assessmentId}`, { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Create assessment
export const createAssessment = createAsyncThunk(
  'agencyAssessments/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-assessments-create', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Wizard-draft lifecycle: the launch wizard creates a draft assessment as
// soon as the user picks the first file (so uploads have a target id),
// then either finalises it on Launch or discards it on Back / skill change.
export const createDraftAssessment = createAsyncThunk(
  'agencyAssessments/createDraft',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-assessments-draft-create', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const finalizeDraftAssessment = createAsyncThunk(
  'agencyAssessments/finalizeDraft',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-assessments-finalize', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const discardDraftAssessment = createAsyncThunk(
  'agencyAssessments/discardDraft',
  async ({ assessmentId }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-assessments-discard', {
        method: 'POST',
        body: JSON.stringify({ assessmentId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Fetch assessment types
export const fetchAssessmentTypes = createAsyncThunk(
  'agencyAssessments/fetchTypes',
  async (team = null, { rejectWithValue }) => {
    try {
      const qs = team ? `?team=${team}` : '';
      const response = await authFetch(`/.netlify/functions/agency-assessment-types-list${qs}`, { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Execute a task
export const executeTask = createAsyncThunk(
  'agencyAssessments/executeTask',
  async ({ assessmentId, taskId, humanGuidance }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-tasks-execute', {
        method: 'POST',
        body: JSON.stringify({ assessmentId, taskId, humanGuidance }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Workbench: review-state tasks across all assessments (Risk Manager dashboard)
export const fetchWorkbench = createAsyncThunk(
  'agencyAssessments/fetchWorkbench',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-workbench-list', { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Knowledge counts for the dashboard (frameworks/policies/threats/taxonomy)
export const fetchKnowledgeCounts = createAsyncThunk(
  'agencyAssessments/fetchKnowledgeCounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-knowledge-counts', { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Approve or reject a task
export const approveTask = createAsyncThunk(
  'agencyAssessments/approveTask',
  async ({ assessmentId, taskId, action, humanGuidance }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-tasks-approve', {
        method: 'POST',
        body: JSON.stringify({ assessmentId, taskId, action, humanGuidance }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const cancelTask = createAsyncThunk(
  'agencyAssessments/cancelTask',
  async ({ assessmentId, taskId, reason }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-tasks-cancel', {
        method: 'POST',
        body: JSON.stringify({ assessmentId, taskId, reason }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const agencyAssessmentsSlice = createSlice({
  name: 'agencyAssessments',
  initialState: {
    items: [],
    assessmentTypes: [],
    activeDetail: null,  // Full assessment state (tasks, findings, artifacts)
    dashboardStats: null, // { stats, recentAssessments }
    workbench: [],        // tasks awaiting review across all assessments
    workbenchLoading: false,
    knowledgeCounts: null, // { frameworks, policies, threat_profiles, risk_taxonomies }
    knowledgeLoading: false,
    loading: false,
    creating: false,
    executing: false,
    dashboardLoading: false,
    error: null,
  },
  reducers: {
    clearAgencyError: (state) => { state.error = null; },
    clearActiveDetail: (state) => { state.activeDetail = null; },

    // Optimistic flip on Execute click. The server eventually flips
    // task.status='running' and resets activity[]=[] when huginnManager
    // boots, but that's 500-2000ms after the click. Doing it client-side
    // immediately means the activity feed renders without dead air, and
    // any stale entries from a previous failed run vanish at click time.
    // The first synthetic entry is replaced by real server entries on
    // the next polling refetch.
    optimisticExecuteTask: (state, action) => {
      const { taskId } = action.payload;
      if (!state.activeDetail?.tasks) return;
      const task = state.activeDetail.tasks.find((t) => t.id === taskId);
      if (!task) return;
      // Capture the click moment as a single ISO string so the entry's
      // timestamp is unambiguous (we've seen reports of "00:00:00"
      // displays where this evaluation happened in surprising scopes).
      const nowIso = new Date().toISOString();
      task.status = 'running';
      task.activity = [
        { at: nowIso, type: 'starting', message: 'Sending task to agent…' },
      ];
      task.output_summary = null;
      task.error_message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard stats
      .addCase(fetchDashboardStats.pending, (state) => { state.dashboardLoading = true; })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state) => { state.dashboardLoading = false; })
      // Fetch all
      .addCase(fetchAssessments.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAssessments.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.assessments || [];
      })
      .addCase(fetchAssessments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch assessments';
      })
      // Fetch detail
      .addCase(fetchAssessmentDetail.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAssessmentDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.activeDetail = action.payload;
      })
      .addCase(fetchAssessmentDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch detail';
      })
      // Create
      .addCase(createAssessment.pending, (state) => { state.creating = true; state.error = null; })
      .addCase(createAssessment.fulfilled, (state, action) => {
        state.creating = false;
        if (action.payload.assessment) {
          state.items.unshift({ ...action.payload.assessment, taskProgress: { total: action.payload.tasks?.length || 0, completed: 0 }, findingCount: 0 });
        }
      })
      .addCase(createAssessment.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload || 'Failed to create assessment';
      })
      // Assessment types
      .addCase(fetchAssessmentTypes.fulfilled, (state, action) => {
        state.assessmentTypes = action.payload.assessmentTypes || [];
      })
      // Execute task
      .addCase(executeTask.pending, (state) => { state.executing = true; state.error = null; })
      .addCase(executeTask.fulfilled, (state, action) => {
        state.executing = false;
        // Update task in activeDetail
        if (state.activeDetail?.tasks) {
          const idx = state.activeDetail.tasks.findIndex(t => t.id === action.meta.arg.taskId);
          if (idx >= 0) {
            state.activeDetail.tasks[idx].status = 'review';
            state.activeDetail.tasks[idx].output_summary = action.payload.outputSummary;
          }
        }
      })
      .addCase(executeTask.rejected, (state, action) => {
        state.executing = false;
        state.error = action.payload || 'Failed to execute task';
      })
      // Workbench
      .addCase(fetchWorkbench.pending, (state) => { state.workbenchLoading = true; })
      .addCase(fetchWorkbench.fulfilled, (state, action) => {
        state.workbenchLoading = false;
        state.workbench = action.payload.workbench || [];
      })
      .addCase(fetchWorkbench.rejected, (state) => { state.workbenchLoading = false; })
      // Knowledge counts
      .addCase(fetchKnowledgeCounts.pending, (state) => { state.knowledgeLoading = true; })
      .addCase(fetchKnowledgeCounts.fulfilled, (state, action) => {
        state.knowledgeLoading = false;
        state.knowledgeCounts = action.payload || null;
      })
      .addCase(fetchKnowledgeCounts.rejected, (state) => { state.knowledgeLoading = false; })
      // Approve task
      .addCase(approveTask.fulfilled, (state, action) => {
        if (state.activeDetail?.tasks) {
          const taskId = action.meta.arg.taskId;
          const taskAction = action.meta.arg.action;
          const idx = state.activeDetail.tasks.findIndex(t => t.id === taskId);
          if (idx >= 0) {
            state.activeDetail.tasks[idx].status = taskAction === 'approve' ? 'completed' : 'ready';
          }
          // If approved, mark next pending task as ready
          if (taskAction === 'approve') {
            const nextPending = state.activeDetail.tasks.find(t => t.status === 'pending');
            if (nextPending) nextPending.status = 'ready';
          }
        }
      });
  },
});

export const { clearAgencyError, clearActiveDetail, optimisticExecuteTask } = agencyAssessmentsSlice.actions;

// Selectors
export const selectAssessments = (state) => state.agencyAssessments?.items || [];
export const selectAssessmentTypes = (state) => state.agencyAssessments?.assessmentTypes || [];
export const selectActiveDetail = (state) => state.agencyAssessments?.activeDetail || null;
export const selectAgencyLoading = (state) => state.agencyAssessments?.loading || false;
export const selectAgencyCreating = (state) => state.agencyAssessments?.creating || false;
export const selectAgencyExecuting = (state) => state.agencyAssessments?.executing || false;
export const selectAgencyError = (state) => state.agencyAssessments?.error || null;
export const selectDashboardStats = (state) => state.agencyAssessments?.dashboardStats || null;
export const selectDashboardLoading = (state) => state.agencyAssessments?.dashboardLoading || false;
export const selectWorkbench = (state) => state.agencyAssessments?.workbench || [];
export const selectWorkbenchLoading = (state) => state.agencyAssessments?.workbenchLoading || false;
export const selectKnowledgeCounts = (state) => state.agencyAssessments?.knowledgeCounts || null;
export const selectKnowledgeLoading = (state) => state.agencyAssessments?.knowledgeLoading || false;

export default agencyAssessmentsSlice.reducer;
